<script setup lang="ts">
import { onMounted, ref, shallowRef } from "vue";
import { useApiClient } from "@/api/client";
import TableNominalValues from "@/modules/dimension_values/components/TableNominalValues.vue";
import useDialog from "@/ui/composables/useDialog";
import { toastApiResult } from "@/toaster";
import { t } from "@/plugins/i18n";
import router from "@/router/router";
import MainBreadcrumbs from "@/ui/components/header/MainBreadcrumbs.vue";

type DimensionValuesPutDTO = App.Data.DimensionValuesPutDTO;

// ============================================================================

const client = useApiClient("dimensions");
const dialog = useDialog();
const dimensionId = parseInt(router.currentRoute.value.params.id as string);
const dimensionNameClean = router.currentRoute.value.params.name_clean;
const loaded = shallowRef(false);
const loading = shallowRef(false);

const breadcrumbs = [
	{
		title: "Dimension Values",
		disabled: false,
		to: "/dimension-values",
	},
	{
		title: t(`properties.${dimensionNameClean}.title`, "Nominal Values"),
		disabled: true,
		to: "/dimension-values",
	},
];

const nominalValues = ref<DimensionValuesPutDTO[]>([]);
const useDefaultSorting = ref<boolean>(true);

const updateDimension = async () => {
	loading.value = true;
	await toastApiResult(
		client.setNominalValues(dimensionId, {
			use_default_sorting: useDefaultSorting.value,
			values: nominalValues.value,
		})
	);
	loading.value = false;
};

onMounted(async () => {
	const response = await client.getNominalValues(
		{},
		[dimensionId],
		null,
		true
	);

	if (!response.isOk()) {
		await dialog.alert({
			title: "Could not get dimension values",
			message: "",
		});
		return;
	}

	useDefaultSorting.value =
		response.value[0]?.dimension?.use_default_sorting ?? true;

	nominalValues.value = response.value.map((value) => {
		return {
			dimension_value_id: value.id,
			label: value.label,
			sort_order: value.sort_order,
			hex_color_value: value.hex_color_value,
		};
	});

	loaded.value = true;
});
</script>

<template>
	<main-breadcrumbs :breadcrumbs="breadcrumbs" />

	<v-card v-if="loaded" class="px-4 pt-4 pb-2">
		<TableNominalValues
			v-model:loading="loading"
			v-model:values="nominalValues"
			v-model:use-default-sorting="useDefaultSorting"
			@save="updateDimension"
		/>
	</v-card>
</template>
